import { createTheme, type Theme, type ThemeOptions } from "@mui/material/styles";
import type { ThemeConfig } from "./themeContract";

/**
 * Turns a ThemeConfig into a MUI theme plus the token object components import.
 * This file is theme-agnostic — it should never contain a literal colour.
 */
export interface ZintoroTokens extends ThemeConfig {}

export const buildTokens = (cfg: ThemeConfig): ZintoroTokens => cfg;

export const buildEyebrowSx = (cfg: ThemeConfig) => ({
  fontFamily: cfg.fonts.mono,
  fontSize: cfg.typography.eyebrow,
  letterSpacing: cfg.typography.eyebrowTracking,
  textTransform: "uppercase" as const,
  color: cfg.color.muted
});

export const numericSx = { fontVariantNumeric: "tabular-nums" as const };

export const createZintoroTheme = (cfg: ThemeConfig): Theme => {
  const c = cfg.color;

  const palette: ThemeOptions["palette"] = {
    primary: { main: c.primary, dark: c.railBg, contrastText: "#fff" },
    success: { main: c.positive.main, dark: c.positive.text },
    warning: { main: c.caution.main, dark: c.caution.text },
    error: { main: c.negative.main, dark: c.negative.text },
    text: { primary: c.ink, secondary: c.body, disabled: c.faint },
    divider: c.border,
    background: { default: c.canvas, paper: c.surface }
  };

  // ── LEGACY — SCHEDULED FOR DELETION ──────────────────────────────────────
  // The original app had NO ThemeProvider: it ran on stock MUI, and every navy
  // in it came from an inline sx, not from the palette. So overriding the
  // palette here is itself a divergence — MUI's default primary is what gave
  // the outlined Portal Management button its visible border on the navy bar,
  // and the default white body is what made the tinted content field read as
  // pale blue rather than grey.
  //
  // NOTHING is passed — not even the font family, and that is deliberate.
  //
  // `cfg.fonts.sans` for legacy IS MUI's default stack, only written without
  // the quotes. But passing ANY fontFamily makes createTypography drop
  // `letterSpacing` from every variant: it emits those metrics only for its own
  // default font, since they are tuned to it. So declaring the default stack
  // explicitly silently stripped 0.02857em from every Button and 0.0075em from
  // every h6, app-wide — text read thinner and ran narrower than the original,
  // which is the exact opposite of this theme's purpose.
  //
  // The original had no ThemeProvider at all, so stock MUI is the target and
  // `createTheme()` hits it exactly. tokens.color.* still drives the legacy
  // chrome components, as the original's inline styles did. Returning here
  // rather than branching inside the options object means nothing below can
  // affect another theme.
  if (cfg.shell === "legacy") {
    return createTheme();
  }

  const options: ThemeOptions = {
    palette,
    shape: { borderRadius: cfg.radius.control },
    typography: {
      fontFamily: cfg.fonts.sans,
      button: {
        textTransform: cfg.typography.buttonTextTransform,
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: cfg.typography.buttonTextTransform === "uppercase" ? ".02857em" : 0
      },
      h1: { fontSize: cfg.typography.pageTitle, fontWeight: cfg.typography.headingWeight, letterSpacing: "-0.01em" },
      h2: { fontSize: cfg.typography.screenTitle, fontWeight: cfg.typography.headingWeight, letterSpacing: "-0.015em" },
      h3: { fontSize: cfg.typography.barTitle, fontWeight: cfg.typography.headingWeight, letterSpacing: "-0.01em" },
      h4: { fontSize: cfg.typography.cardTitle, fontWeight: cfg.typography.headingWeight },
      h5: { fontSize: cfg.typography.sectionTitle, fontWeight: cfg.typography.headingWeight },
      h6: { fontSize: cfg.typography.labelTitle, fontWeight: cfg.typography.headingWeight },
      body1: { fontSize: cfg.typography.body + 1, lineHeight: 1.55 },
      body2: { fontSize: cfg.typography.body, lineHeight: 1.5 },
      caption: { fontSize: cfg.typography.caption, color: c.muted }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: c.canvas },
          // Chrome paints autofilled fields its own yellow, which overrides the
          // palette on every form in the app. The inset shadow is the only way
          // to repaint the background of an autofilled input.
          "input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus":
            {
              WebkitBoxShadow: `0 0 0 100px ${c.surface} inset`,
              WebkitTextFillColor: c.ink,
              caretColor: c.ink
            }
        }
      },
      MuiPaper: {
        defaultProps: { elevation: cfg.fidelity === "mui" ? 1 : 0 },
        styleOverrides: {
          root: {
            borderRadius: cfg.radius.card,
            backgroundImage: "none",
            ...(cfg.fidelity === "custom"
              ? { border: `1px solid ${c.border}`, boxShadow: cfg.shadow.card }
              : {})
          }
        }
      },
      MuiButton: {
        defaultProps: { disableElevation: cfg.fidelity === "custom" },
        styleOverrides: {
          root: { borderRadius: cfg.radius.control, minHeight: 36, paddingInline: 14 },
          // Primary is the ONLY action colour in every theme.
          containedPrimary: { backgroundColor: c.primary, "&:hover": { backgroundColor: c.primaryHover } },
          outlined: {
            borderColor: c.borderHover,
            color: c.primary,
            "&:hover": { borderColor: c.primary, backgroundColor: "#F5F9FC" }
          },
          text: { color: c.primary }
        }
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: cfg.radius.pill, fontWeight: 600, fontSize: 12 } }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: cfg.radius.control,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: c.borderHover },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: c.primary }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: "1px solid #F1F4F7", fontSize: cfg.typography.body, padding: "13px 14px" },
          head: {
            fontFamily: cfg.fonts.mono,
            fontSize: cfg.typography.eyebrow,
            letterSpacing: cfg.typography.eyebrowTracking,
            textTransform: "uppercase",
            fontWeight: 500,
            color: c.muted,
            backgroundColor: "#F7F9FB",
            borderBottom: `1px solid ${c.border}`,
            padding: "11px 14px"
          }
        }
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { backgroundColor: c.ink, fontSize: 12, borderRadius: 6, padding: "6px 10px" } }
      }
    }
  };

  return createTheme(options);
};

export default createZintoroTheme;
